import { Observable, catchError, defer, finalize, map, of, shareReplay } from 'rxjs';
import { LazyCache } from './cache';
import { TimeSpan } from '../model/date.helper';

export type RequestCacheOptions<T> = {
  ttlMs?: number; // Optional cache TTL in milliseconds
  errorHandler?: (error: unknown) => Observable<T>;
};

export class RequestCache<T> {
  private readonly cache = new LazyCache<T>();
  private readonly pendingRequests = new Map<string, Observable<T>>();
  private readonly defaultTtlMs = TimeSpan.fromMinutes(1);

  public clear() { this.cache.clear(); }

  /**
   * Execute a request with caching and deduplication
   *
   * @param requestKey - Unique identifier to use for the cache on the request
   * @param requestFn - Function that returns an Observable for the API call
   * @param options - Configuration options
   * @returns Observable that resolves to the cached or fresh response
   */
  public executeRequest<K>(
    requestKey: string,
    requestFn: () => Observable<K>,
    transformer: (response: K) => T,
    options: RequestCacheOptions<T> = {},
  ): Observable<T> {
    if (this.cache.has(requestKey)) {
      return of(this.cache.get(requestKey));
    }

    return this.cache.fetchWithCache(
      requestKey,
      defer(() => {
        const pendingRequest = this.pendingRequests.get(requestKey);
        if (pendingRequest) {
          return pendingRequest;
        }

        const request$ = requestFn().pipe(
          map((response) => {
            return transformer(response);
          }),
          finalize(() => {
            this.pendingRequests.delete(requestKey);
          }),
          catchError(options?.errorHandler ?? ((_error: unknown) => of(null))),
          shareReplay(1),
        );

        this.pendingRequests.set(requestKey, request$);
        return request$;
      }),
      { expirationMs: options?.ttlMs ?? this.defaultTtlMs },
    );
  }
}
